import { useEffect, useCallback } from 'react';

/**
 * Custom hook to handle browser back button when a modal is open
 * 
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Function to close the modal
 * @param {boolean} showWarning - Whether to show a confirmation dialog (defaults to true)
 * @param {string} warningMessage - Custom warning message
 * @returns {void}
 */
export const useModalBackHandler = (
  isOpen,
  onClose,
  showWarning = true,
  warningMessage = "Changes you made may not be saved. Are you sure you want to go back?"
) => {
  const handlePopState = useCallback((event) => {
    // Only handle if modal is open
    if (isOpen) {
      // Prevent the default back action
      event.preventDefault();
      
      // If warning is enabled, show confirmation dialog
      if (showWarning) {
        const shouldClose = window.confirm(warningMessage);
        if (shouldClose) {
          onClose();
        } else {
          // Push a new state to replace the one we just popped
          window.history.pushState(null, '', window.location.pathname);
        }
      } else {
        // Just close the modal without warning
        onClose();
      }
      
      // Return false to indicate we handled it
      return false;
    }
    return true;
  }, [isOpen, onClose, showWarning, warningMessage]);

  useEffect(() => {
    if (isOpen) {
      // When modal opens, push a new history state
      window.history.pushState(null, '', window.location.pathname);
      
      // Add popstate event listener
      window.addEventListener('popstate', handlePopState);
    }
    
    // Cleanup: remove event listener
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, handlePopState]);
}; 